import { useAuth0 } from '@auth0/auth0-react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { useMockAuth, mockAuth0 } from './mockAuth';
import { clearStoredOrganization, getStoredOrganization } from '../utils/organizationSession';
import { Auth0Client } from '@auth0/auth0-spa-js';


const auth0 = new Auth0Client({
  domain: process.env.REACT_APP_AUTH0_DOMAIN || '',
  clientId: process.env.REACT_APP_AUTH0_CLIENT_ID || '',
  authorizationParams: {
    redirect_uri: window.location.origin,
  },
  useRefreshTokens: true,
  cacheLocation: 'localstorage',
  useRefreshTokensFallback: true
});

// Check if we should use mock authentication
const isMockAuth = process.env.REACT_APP_USE_MOCK_AUTH === 'true';

// Log which authentication mode is being used
if (isMockAuth) {
  console.log('🔑 Using MOCK authentication for development');
} else {
  console.log('🔒 Using real Auth0 authentication');
}

/**
 * Get the current organization_id from localStorage
 * Note: Avoiding direct Redux store access to prevent circular dependencies
 * @returns organization_id if available, null otherwise
 */
const getCurrentOrganizationId = (): string | null => {
  const storedOrg = getStoredOrganization();
  return storedOrg?.organization_id || null;
};

export const login = async () => {
  if (isMockAuth) {
    return mockAuth0.loginWithRedirect();
  }

  // Build authorization params with conditional organization
  const authorizationParams: any = {
    redirect_uri: window.location.origin,
    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
  };
  
  // Add organization_id if available
  const organizationId = getCurrentOrganizationId();
  if (organizationId) {
    authorizationParams.organization = organizationId;
    console.log('🏢 Including organization in login:', organizationId);
  }

  try {
    await auth0.loginWithPopup({
      authorizationParams,
    });
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const logout = async () => {
  if (isMockAuth) {
    // Clear organization data on logout
    clearStoredOrganization();
    return mockAuth0.logout();
  }
  
  try {
    // Clear organization data before logout
    clearStoredOrganization();
    // Clear other persisted state on logout
    localStorage.clear();
    
    await auth0.logout({
      logoutParams: {
        returnTo: window.location.origin,
      }
    });
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};

export const handleRedirectCallback = async () => {
  if (isMockAuth) {
    return mockAuth0.handleRedirectCallback();
  }
  
  try {
    // Get organization_id for consistent callback handling
    const organizationId = getCurrentOrganizationId();
    const options: any = {};
    
    if (organizationId) {
      options.authorizationParams = {
        organization: organizationId
      };
      console.log('🏢 Including organization in handleRedirectCallback:', organizationId);
    }
    
    await auth0.handleRedirectCallback(options);
    const user = await auth0.getUser();
    return user;
  } catch (error) {
    console.error('Error handling redirect:', error);
    throw error;
  }
};

export const getToken = async () => {
  if (isMockAuth) {
    return mockAuth0.getAccessTokenSilently();
  }

  // Build authorization params with conditional organization
  const authorizationParams: any = {
    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
  };
  
  // Add organization_id if available
  const organizationId = getCurrentOrganizationId();
  if (organizationId) {
    authorizationParams.organization = organizationId;
    console.log('🏢 Including organization in getToken:', organizationId);
  }

  try {
    const token = await auth0.getTokenSilently({
      authorizationParams,
    });
    return token;
  } catch (error) {
    console.error('Error getting token:', error);
    throw error;
  }
};

export const checkAuthentication = async () => {
  if (isMockAuth) {
    return mockAuth0.user;
  }
  
  try {
    const isAuth = await auth0.isAuthenticated();
    if (isAuth) {
      return await auth0.getUser();
    }
    return null;
  } catch (error) {
    console.error('Error checking auth:', error);
    return null;
  }
};

export const useAuthService = () => {
  // Use the real Auth0 hook, but conditionally return mock data
  const auth0Context = useAuth0();
  
  // If mock auth is enabled, override the getAccessTokenSilently function
  const getAccessTokenSilently = isMockAuth 
    ? mockAuth0.getAccessTokenSilently 
    : auth0Context.getAccessTokenSilently;

  const getAuthHeaders = async () => {
    try {
      const token = await getAccessTokenSilently();

      return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
    } catch (error) {
      console.error('Error getting auth headers:', error);
      return {
        'Content-Type': 'application/json',
      };
    }
  };

  return {
    getAuthHeaders,
    login,
    logout,
    handleRedirectCallback,
    getToken,
    checkAuthentication,
  };
};
